import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { pDescription } from '../interfaces/pDescription';
import { WebAddresses } from '../app.config';
import { openSnackBar } from '../helpers/genericFunctions';
import { ApiResponse } from '../interfaces/api-response';
import { MatSnackBar } from '@angular/material/snack-bar';
import { globalUser } from '../helpers/globalFields';

/**
   * interacts with pDescription API. pDescriptions contain the interaction between the end user and support aswell as meta information about changes
   * that have been made to the ticket or user information.
 */
@Injectable({
  providedIn: 'root'
})
export class PDescriptionService {
  /**
   * constructor
   * 
   * @param http The HttpClient that GET, POST, PUT, DELETE on the api
   * @param snackBar Overlay to display errors and success messages to end use
   */
  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar
  ) { }

  /**
   * allows easy switching between production and development webserver addresses for api
   */
  private baseUrl = WebAddresses.tCCTicketServer;

  /**
   * creates a new pDescription
   * 
   * @param data pDescription content (subset of pDescription interface)
   * @returns new pDescription
   */
  create(data: any): Observable<any> {
    return this.http.post<ApiResponse>(`${this.baseUrl}/api/pdescription/create`, data, {
      headers: globalUser.getHeaders()
    }).pipe(
      tap(response => {
        if (response.status != "success") {
          openSnackBar(response.message, 10000, this.snackBar);
        }
      }),
      map(response => response.data ?? []),
      catchError(error => {
        console.error("Error creating pDescription:", error);
        return throwError(() => new Error("Failed to create pDescription."));
      })
    );
  }

  /**
   * returns all pDescriptions that belong to a ticket
   * 
   * @param pticket_id id of pTicket 
   * @returns list of all pDescriptions that belong to the requested ticket
   */
  getByPTicketID(pticket_id: number): Observable<pDescription[]> {
    return this.http.get<ApiResponse>(`${this.baseUrl}/api/pdescription/findbyticketid/${pticket_id}`, {
      headers: globalUser.getHeaders()
    }).pipe(
      tap(response => {
        if (response.status != "success") {
          openSnackBar(response.message, 10000, this.snackBar);
        }
      }),
      map(response => response.data ?? []),
      catchError(error => {
        console.error("Error fetching pDescription by pTicket Id:", error);
        return throwError(() => new Error("Failed to fetch pDescription by pTicket Id."));
      })
    );
  }

  /**
   * updates a pDescription. Allows changes to the communicator type or content.
   * 
   * @param data subset of pDescription interface parameters that are to be updated
   * @param pDescriptionID id of the pDescription that is to be updated
   * @returns updated pDescription
   */
  update(data: any, pDescriptionID: number): Observable<any> {
    return this.http.put<ApiResponse>(`${this.baseUrl}/api/pdescription/update/${pDescriptionID}`, data, {
      headers: globalUser.getHeaders()
    }).pipe(
      tap(response => {
        if (response.status != "success") {
          openSnackBar(response.message, 10000, this.snackBar);
        }
      }),
      map(response => response.data ?? []),
      catchError(error => {
        console.error("Error updating pDescription:", error);
        return throwError(() => new Error("Failed to update pDescription."));
      })
    );
  }

  /**
   * deletes pDescription. Allows for mistakenly inputed pDescriptions to be completely removed from the database
   * 
   * @param pDescriptionID id of the pDescription
   * @returns returns the deleted pDescription (for reverting decision if needed)
   */
  delete(pDescriptionID: number): Observable<any> {
    return this.http.delete<ApiResponse>(`${this.baseUrl}/api/pdescription/delete/${pDescriptionID}`, {
      headers: globalUser.getHeaders()
    }).pipe(
      tap(response => {
        if (response.status != "success") {
          openSnackBar(response.message, 10000, this.snackBar);
        }
      }),
      map(response => response.data ?? []),
      catchError(error => {
        console.error("Error deleting pDescription:", error);
        return throwError(() => new Error("Failed to delete pDescription."));
      })
    );
  }
}
